import { findBookmarkName } from "@via/pages/data/file-organizer/helpers.ts";
import { FileOrganizerProvider } from "@via/context/file-organizer-context.tsx";
import { useSshDataConnection } from "@via/hooks/useSshDataConnection.tsx";
import NoActiveConnection from "@via/pages/data/data-sources/cloud-data-sources/common/no-active-connection.tsx";
import DataSourceTitle from "@via/pages/data/data-sources/data-source-title.tsx";
import FileOrganizer from "@via/pages/data/file-organizer/file-organizer.tsx";
import { DataSourceComponentView } from "@via/utils/interfaces.tsx";
import {
  type AgentUploadState,
  CredentialView,
  DataSourceProvider,
  type SelectedFile,
} from "common";
import { type JSX, useEffect, useState } from "react";
import { useParams, useSearchParams } from "react-router-dom";
import ClusterServerLogo from "/cluster-server-logo.svg";
import SSHFolderBrowser from "@via/pages/data/data-sources/ssh-data-source/ssh-folder-browser.tsx";
import { useGetDataSourceList } from "@via/pages/data/data-connections/data-connections.api.tsx";
import {
  getDirForSsh,
  prepareBucketPath,
} from "@via/pages/data/data-sources/data-source.api.tsx";
import { BucketObject } from "@via/pages/data/data-sources/utils/helpers.tsx";
import { ArrowUpTrayIcon } from "@heroicons/react/24/outline";
import { useGetSavedCredentials } from "@via/pages/account/profile/profile.api.tsx";
import {
  agentUploadButtonView,
  isAgentEnvironment,
} from "@via/pages/data/data-sources/ssh-data-source/agent-listing-notices.ts";
import { getDocsUrl } from "@via/utils/config.tsx";
import { AGENT_UPGRADE_DOCS_PATH } from "@via/utils/constants.tsx";

function SshData(): JSX.Element {
  const [searchParams] = useSearchParams();
  const bookmarkId = searchParams.get("bookmark");
  const { data: bookmarkList } = useGetDataSourceList();

  useEffect(() => {
    if (bookmarkId && bookmarkList?.length) {
      const selectedDatasource = bookmarkList.find(
        item => item.id === Number(bookmarkId)
      );

      const bookmarkCredentials = {
        savedCredentialId: selectedDatasource?.credentialId || 0,
        bucketName: selectedDatasource?.path || "",
      };
      prepareBucketPath(bookmarkCredentials);
      setConnectionCredentials({
        ...bookmarkCredentials,
        prefix: undefined,
        bucketName: getDirForSsh(bookmarkCredentials),
      });
    }
  }, [bookmarkId, bookmarkList]);

  const [currentView, setCurrentView] = useState<DataSourceComponentView>(
    DataSourceComponentView.FolderBrowser
  );
  const [hasConnection, setHasConnection] = useState(false);

  const [selectedFiles, setSelectedFiles] = useState<SelectedFile[]>([]);
  const [globalBucketName] = useState("");
  const [cart, setCart] = useState<BucketObject[]>([]);
  const [uploadModalOpen, setUploadModalOpen] = useState(false);
  const [agentUploadState, setAgentUploadState] = useState<
    AgentUploadState | undefined
  >();

  const updateSelectedFiles = (selectedFileList: SelectedFile[]): void => {
    setSelectedFiles(
      selectedFileList.map(file => ({
        ...file,
        id: file.name,
      }))
    );
    setCurrentView(DataSourceComponentView.FileOrganizer);
  };

  const setCurrentViewToFolderBrowser = () => {
    setCurrentView(DataSourceComponentView.FolderBrowser);
  };

  const { connectionCredentials, setConnectionCredentials } =
    useSshDataConnection();

  useEffect(() => {
    if (connectionCredentials) {
      setHasConnection(true);
      setCurrentView(DataSourceComponentView.FolderBrowser);
    }
  }, [connectionCredentials]);

  // agent-5-5: on an agent source the button follows the browse answer's upload state.
  const { data: savedCredentials } = useGetSavedCredentials({
    type: CredentialView.UserOwned,
  });
  const isAgentSource = isAgentEnvironment(
    savedCredentials,
    connectionCredentials?.savedCredentialId
  );

  const agentButton = agentUploadButtonView(agentUploadState);
  const showUpload =
    hasConnection &&
    currentView === DataSourceComponentView.FolderBrowser &&
    (!isAgentSource || agentButton.visible);
  const uploadDisabled = isAgentSource && !agentButton.enabled;

  const uploadButton = showUpload ? (
    <div className="flex flex-col items-end gap-1">
      <button
        data-testid="folderBrowserUploadButton"
        onClick={() => setUploadModalOpen(true)}
        disabled={uploadDisabled}
        className="inline-flex items-center gap-2 rounded-md border border-indigo-600 bg-white px-3 py-2 text-sm font-semibold text-indigo-600 hover:bg-indigo-50 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-indigo-600 disabled:cursor-not-allowed disabled:opacity-50">
        <ArrowUpTrayIcon className="h-5 w-5" />
        Upload File(s)
      </button>
      {isAgentSource && agentButton.reason ? (
        <p
          data-testid="folderBrowserUploadReason"
          className="max-w-xs text-right text-xs text-gray-500">
          {agentButton.reason}
          {agentButton.docsLink ? (
            <>
              {" "}
              <a
                href={getDocsUrl(AGENT_UPGRADE_DOCS_PATH)}
                target="_blank"
                rel="noreferrer"
                className="underline">
                How to update the agent
              </a>
            </>
          ) : null}
        </p>
      ) : null}
    </div>
  ) : null;

  return (
    <div className="flex-auto">
      {
        <div className="ml-2">
          <DataSourceTitle
            logoPath={ClusterServerLogo}
            title="Cluster / Server"
            actions={uploadButton}
          />
          {currentView === DataSourceComponentView.FileOrganizer ? (
            <FileOrganizerProvider>
              <FileOrganizer
                importedFiles={selectedFiles}
                globalBucketName={globalBucketName}
                onBackToSelectionClick={setCurrentViewToFolderBrowser}
                credentialId={null}
                dataSourceId={bookmarkId ? Number(bookmarkId) : undefined}
                dataSourceName={findBookmarkName(bookmarkList, bookmarkId)}
                provider={DataSourceProvider.SSH}
                runEnvId={connectionCredentials?.savedCredentialId}
              />
            </FileOrganizerProvider>
          ) : (
            <div>
              {hasConnection ? (
                <SSHFolderBrowser
                  onContinue={updateSelectedFiles}
                  cart={cart}
                  setCart={setCart}
                  uploadModalOpen={uploadModalOpen}
                  setUploadModalOpen={setUploadModalOpen}
                  isBookmark={Boolean(bookmarkId)}
                  dataSourceId={bookmarkId ? Number(bookmarkId) : undefined}
                  onAgentUploadStateChange={setAgentUploadState}
                />
              ) : (
                <NoActiveConnection genericErrorMessage="This environment is not accessible, please check your internet connection" />
              )}
            </div>
          )}
        </div>
      }
    </div>
  );
}

export const SshDataWrapper = () => {
  const { dataSourceName } = useParams();
  return <SshData key={dataSourceName} />;
};
